import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ThinLineFlag } from '../../hooks/useThinLineCheck';

interface ThinLineDetailsModalProps {
  flags: ThinLineFlag[];
  // Which flag to show first — from a layer badge or background-tool click.
  // Falls back to the first flag when unset (opened from the top banner).
  initialUid?: string;
  onClose: () => void;
  // "Upload another logo"/"Replace" — routes back into the relevant tool.
  // Undefined for the background flag's own separate surfacing (see
  // BackgroundTool.tsx, which opens this same modal but doesn't need a
  // generic replace action since its own panel already IS the replace UI).
  onReplace?: (flag: ThinLineFlag) => void;
}

// Mirrors the client-provided FastEditor reference screenshot: a modal
// showing the flagged artwork with red dots at the too-thin points, "Red
// dots indicate where lines are too thin" caption, and Continue/Upload
// another actions. Chrome (overlay, card, header) matches this codebase's
// own ImageCompareModal.tsx for visual consistency (var(--border)/
// var(--selection)/var(--text-muted) tokens, borderRadius 10).
export function ThinLineDetailsModal({ flags, initialUid, onClose, onReplace }: ThinLineDetailsModalProps) {
  const { t } = useTranslation();
  const [activeUid, setActiveUid] = useState(initialUid ?? flags[0]?.uid);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const active = flags.find(f => f.uid === activeUid) ?? flags[0];
  if (!active) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: 10, width: 'min(640px, 100%)', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '20px 24px 0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>{t('thinLine.modalTitle')}</h2>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '6px 0 0', maxWidth: 440 }}>
                {t('thinLine.modalDescription')}
              </p>
            </div>
            <button onClick={onClose} title={t('common.close')}
              style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '2px 6px' }}>
              ×
            </button>
          </div>

          {flags.length > 1 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 16, flexWrap: 'wrap' }}>
              {flags.map(f => (
                <button
                  key={f.uid}
                  onClick={() => setActiveUid(f.uid)}
                  style={{
                    fontSize: 12, padding: '5px 10px', borderRadius: 5, cursor: 'pointer',
                    border: '1px solid var(--border)',
                    background: f.uid === active.uid ? 'var(--selection)' : 'transparent',
                    color: f.uid === active.uid ? 'white' : 'var(--text-muted)',
                  }}
                >
                  {f.isBackground ? t('thinLine.backgroundLabel') : t('thinLine.itemLabel')}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: '20px 24px', overflow: 'auto' }}>
          <ThinLineImagePreview flag={active} />
          <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', marginTop: 10 }}>
            {t('thinLine.redDotsCaption')}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose} className="btn btn-secondary" style={{ fontSize: 13 }}>
            {t('thinLine.continueAnyway', { item: active.isBackground ? t('thinLine.backgroundLabel') : t('thinLine.itemLabel') })}
          </button>
          {onReplace && !active.isBackground && (
            <button
              onClick={() => onReplace(active)}
              style={{ fontSize: 13, padding: '8px 16px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
            >
              {t('thinLine.uploadAnother')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ThinLineImagePreview({ flag }: { flag: ThinLineFlag }) {
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    setNaturalSize(null);
    if (!flag.previewUrl) return;
    const img = new Image();
    img.onload = () => setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = flag.previewUrl;
  }, [flag.previewUrl]);

  return (
    <div style={{
      position: 'relative', width: '100%', maxHeight: 360, overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'repeating-conic-gradient(#e2e8f0 0% 25%, #ffffff 0% 50%) 0 0 / 20px 20px',
      borderRadius: 8, border: '1px solid var(--border)',
    }}>
      {flag.previewUrl ? (
        <div style={{ position: 'relative', maxWidth: '100%', maxHeight: 360 }}>
          <img
            src={flag.previewUrl}
            alt=""
            style={{ display: 'block', maxWidth: '100%', maxHeight: 360, objectFit: 'contain' }}
          />
          {naturalSize && flag.result.thinPoints.map((p, i) => (
            // thinPoints are normalized 0..1 against the image's own pixel
            // dimensions (see thinLineDetection.ts) — a plain percentage
            // position works directly against the rendered <img>, since it's
            // displayed at object-fit:contain within its own natural aspect
            // ratio (no separate scale math needed).
            <span
              key={i}
              style={{
                position: 'absolute', left: `${p.x * 100}%`, top: `${p.y * 100}%`,
                width: 8, height: 8, borderRadius: '50%',
                background: '#ef4444', border: '1.5px solid white',
                transform: 'translate(-50%, -50%)',
                boxShadow: '0 0 0 1px rgba(239,68,68,0.4)',
                pointerEvents: 'none',
              }}
            />
          ))}
        </div>
      ) : (
        <span style={{ fontSize: 12, color: 'var(--text-muted)', padding: 24 }} />
      )}
    </div>
  );
}
